import React, { lazy, Suspense, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import ScrollUp from "./components/ScrollUp";
import { Helmet } from 'react-helmet';
import "./AgentHomePage.css";
import "@fontsource/poppins";
import "../src/App.css";
import Header from "./components/Header";
import Body from "./components/Body/Body";
import axios from "axios";

const Lodetails = lazy(() => import("./components/Lodetails/lodetails.js"));
const Lowebsites = lazy(() => import("./components/Lowebsites/index.js"));
const EventHighlights = lazy(() => import("./components/EventHighlights/index.js"));
const Presentations = lazy(() => import("./components/Presentation/presentation.js"));
const BorrowerLeadForm = lazy(() => import("./components/BorrowerLeadForm/BorrowerLeadForm.js"));
const Footer = lazy(() => import("./components/Footer/footer.js"));

function App() {
  const { agentName } = useParams();
  
  // Function to determine agent JSON name based on domain and URL params
  const getAgentJsonName = () => {
    const hostname = window.location.hostname;
    const miniexpocentreDomains = ['miniexpocenter.com', 'development.miniexpocenter.com'];
    
    // Check if current domain is in the miniexpocenter domains list
    if (miniexpocentreDomains.includes(hostname)) {
      return "miniexpocenter.json";
    }
    
    // Default behavior - use agentName from URL params
    return agentName ? agentName.toLowerCase() + ".json" : "miniexpocenter.json";
  };

  const agentJsonName = getAgentJsonName();
  
  const [loading, setLoading] = useState(true);
  const [agentData, setAgentData] = useState();
  const [loanOfficerActive, setLoanOfficerActive] = useState(true);
  const [loanOfficerData, setLoanOfficerData] = useState();
  const [angelAiUserInfo, setAngelAiUserInfo] = useState();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const agentResponse = await axios.get(
          `${process.env.REACT_APP_CDN_LINK}marketing-lo-website/miniexpocenter/${agentJsonName}`
        );
        const responseData = agentResponse.data;
        setAgentData(responseData);
       
        if (responseData) {
          setLoading(false);
          const loanOfficerId = responseData.staff.loanOfficer.lossid;
          await checkifLoanOfficerIsActive(loanOfficerId);
          await fetchAngelAiUserInfo();
          setLoanOfficerData(responseData);
        }
      } catch (error) {
        console.log(error);
      }
    };

    const fetchAngelAiUserInfo = async () => {
      try {
        await axios
          .get(process.env.REACT_APP_ANGEL_AI_WEB_USER_INFO)
          .then((response) => {
            setAngelAiUserInfo(response.data);
          })
          .catch((error) => {
            window.location.replace("https://swmc.com");
          });
      } catch (e) {
        console.log(e);
      }
    };

    const checkifLoanOfficerIsActive = async (loanOfficerId) => {
      try {
        const loanOfficerResponse = await axios.post(
          process.env.REACT_APP_CHECK_LO_ACTIVE,
          { loanOfficerId }
        );
        setLoanOfficerActive(loanOfficerResponse.data.active);
      } catch (error) {
        console.log(error);
      }
    };

    fetchData();
  }, [agentJsonName]); // Added agentJsonName as dependency

  return (
    <>
   {process.env.REACT_APP_HOST_URL_EXPOCENTER === 'true' && loanOfficerData?.metaData && (
  <Helmet>
    <title>{loanOfficerData.metaData.title}</title>
    <meta name="description" content={loanOfficerData.metaData.description} />
    <meta name="keywords" content={loanOfficerData.metaData.keywords} />
    <link rel="canonical" href={loanOfficerData.metaData.url} />
    
    <meta property="og:title" content={loanOfficerData.metaData.title} />
    <meta property="og:description" content={loanOfficerData.metaData.description} />
    
    <meta name="twitter:title" content={loanOfficerData.metaData.title} />
    <meta name="twitter:description" content={loanOfficerData.metaData.description} />
  </Helmet>
)}

  <Helmet>
    <link
      rel="canonical"
      href={`${process.env.PUBLIC_URL}/${agentName}/`}
    />
  </Helmet>

    <React.Fragment>
      <div className="bodyContainer">
        {loading ? (
          <div className="mt-5 mb-3 text-center">
            <Spinner animation="border" />
            <h5>Loading...</h5>
          </div>
        ) : (
          <>
            {agentData && (
              <>
                <Suspense fallback={<div />}>
                  {!loanOfficerData ? (
                    <div className="mt-5 mb-3 text-center">
                      <Spinner animation="border" />
                      <h5>Loading...</h5>
                    </div>
                  ) : (
                    <>
                      <ScrollUp />
                      <Header
                        loanOfficerActive={loanOfficerActive}
                        loanOfficerData={loanOfficerData}
                        angelAiUserInfo={angelAiUserInfo}
                      />
                      <Body
                        loanOfficerActive={loanOfficerActive}
                        loanOfficerData={loanOfficerData}
                        angelAiUserInfo={angelAiUserInfo}
                      />
                      <Lowebsites
                        loanOfficerActive={loanOfficerActive}
                        loanOfficerData={loanOfficerData}
                        angelAiUserInfo={angelAiUserInfo}
                      />
                      <EventHighlights
                        loanOfficerActive={loanOfficerActive}
                        loanOfficerData={loanOfficerData}
                        angelAiUserInfo={angelAiUserInfo}
                      />
                      <Presentations
                        loanOfficerActive={loanOfficerActive}
                        loanOfficerData={loanOfficerData}
                        angelAiUserInfo={angelAiUserInfo}
                      />
                      <Lodetails
                        loanOfficerActive={loanOfficerActive}
                        loanOfficerData={loanOfficerData}
                        angelAiUserInfo={angelAiUserInfo}
                      />
                      <BorrowerLeadForm
                        loanOfficerActive={loanOfficerActive}
                        loanOfficerData={loanOfficerData}
                        angelAiUserInfo={angelAiUserInfo}
                      />
                      <Footer />
                    </>
                  )}
                </Suspense>
              </>
            )}
          </>
        )}
      </div>
    </React.Fragment>
    </>
  );
}

export default App;